import { useEffect } from "react";
import { Link, useParams, Navigate, useLocation } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { BLOG_POSTS, DESTINATION_AIRPORT, buildFlightSearchUrl } from "@/data/blogs";
import { ArrowLeft, ArrowUpRight, CheckCircle, Phone, AirplaneTilt } from "@phosphor-icons/react";
import { PHONE_DISPLAY, PHONE_TEL } from "@/components/PhoneCTA";
import { useSEO, articleSchema, breadcrumbsSchema } from "@/lib/seo";

function DestinationCTA({ destination }) {
  const map = DESTINATION_AIRPORT[destination.anchor];
  if (!map) return null;
  return (
    <div
      className="mt-8 bg-[#0A0A0B] text-[#F7F5F0] p-6 md:p-8 max-w-3xl flex flex-col gap-4"
      data-testid={`destination-cta-${destination.anchor}`}
    >
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <p className="tcp-overline text-[#C29B62] mb-2">Ready to fly?</p>
          <p className="font-serif text-xl md:text-2xl tracking-tight leading-snug">
            Search flights to <em className="italic">{destination.name}</em>
            <span className="text-[#A09E9A] text-base ml-2 font-sans not-italic">
              ({map.city} · {map.code})
            </span>
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Link
            to={buildFlightSearchUrl(destination.anchor)}
            data-testid={`destination-cta-search-${destination.anchor}`}
            className="btn-gold inline-flex items-center gap-2 text-xs tracking-[0.16em] uppercase whitespace-nowrap"
          >
            <AirplaneTilt size={14} weight="fill" /> Search flights
          </Link>
          <a
            href={`tel:${PHONE_TEL}`}
            data-testid={`destination-cta-call-${destination.anchor}`}
            className="inline-flex items-center gap-2 text-xs tracking-[0.16em] uppercase text-[#A09E9A] hover:text-[#F7F5F0] whitespace-nowrap"
          >
            <Phone size={14} /> or call concierge
          </a>
        </div>
      </div>
      <div className="pt-3 border-t border-white/10 flex items-center justify-between gap-3">
        <p className="text-xs tracking-[0.18em] uppercase text-[#666460]">More about {map.city}</p>
        <Link
          to={`/destinations/${map.code}`}
          data-testid={`destination-cta-guide-${destination.anchor}`}
          className="inline-flex items-center gap-2 text-xs tracking-[0.16em] uppercase text-[#C29B62] hover:text-[#A88550] border-b border-[#C29B62] hover:border-[#A88550] pb-0.5 transition-colors"
        >
          Read the {map.city} guide <ArrowUpRight size={14} />
        </Link>
      </div>
    </div>
  );
}

export default function BlogPost() {
  const { slug } = useParams();
  const location = useLocation();
  const post = BLOG_POSTS[slug];

  useSEO(
    post
      ? {
          title: `${post.title} | TheCityPair Journal`,
          description: post.excerpt,
          canonical: `/blog/${slug}`,
          og: { type: "article", image: post.hero },
          jsonLd: [
            articleSchema(post),
            breadcrumbsSchema([
              { name: "Home", url: "/" },
              { name: "Journal", url: "/journal" },
              { name: post.title, url: `/blog/${slug}` },
            ]),
          ],
        }
      : { title: "Story not found | TheCityPair", noindex: true }
  );

  useEffect(() => {
    if (post && location.hash) {
      const el = document.getElementById(location.hash.slice(1));
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
    } else {
      window.scrollTo(0, 0);
    }
  }, [post, location.hash, slug]);

  if (!post) return <Navigate to="/" replace />;

  // Fast lookup: which sections are flight-bookable destinations?
  const destByAnchor = Object.fromEntries(post.destinations.map((d) => [d.anchor, d]));

  return (
    <div className="bg-[#F7F5F0] min-h-screen" data-testid="blog-post">
      <div className="bg-[#0A0A0B] relative"><Navbar dark /><div className="h-24" /></div>

      <article className="max-w-5xl mx-auto px-6 md:px-12 py-12 md:py-20">
        <Link to="/" className="text-xs tracking-[0.18em] uppercase text-[#666460] hover:text-[#C29B62] inline-flex items-center gap-2 mb-8" data-testid="blog-back">
          <ArrowLeft size={14} /> Back to home
        </Link>

        <p className="tcp-overline text-[#C29B62] mb-4">Travel Stories</p>
        <h1 className="font-serif text-4xl md:text-6xl tracking-tighter leading-[0.95] text-[#1C1C1C]">
          {post.title}
        </h1>
        <p className="font-serif text-2xl md:text-3xl italic text-[#666460] mt-3">{post.subtitle}</p>
        <div className="flex items-center gap-4 mt-6 text-xs tracking-[0.18em] uppercase text-[#666460]">
          <span>{post.author}</span>
          <span>·</span>
          <span>{post.published_at}</span>
          <span>·</span>
          <span>{post.read_minutes} min read</span>
        </div>

        <div className="my-10 md:my-12 aspect-video w-full overflow-hidden">
          <img src={post.hero} alt={post.title} className="w-full h-full object-cover" data-testid="blog-hero" />
        </div>

        <p className="text-lg leading-relaxed text-[#1C1C1C] max-w-3xl mb-12">
          {post.excerpt}
        </p>

        {/* Anchor strip */}
        <div className="flex flex-wrap gap-2 mb-12 pb-8 border-b border-[#E0DDD6]" data-testid="blog-anchors">
          {post.destinations.map((d) => (
            <a
              key={d.anchor}
              href={`#${d.anchor}`}
              data-testid={`blog-anchor-${d.anchor}`}
              className="px-3 py-1.5 text-xs tracking-[0.16em] uppercase border border-[#E0DDD6] text-[#666460] hover:border-[#C29B62] hover:text-[#C29B62] transition-colors"
            >
              {d.name}
            </a>
          ))}
        </div>

        {post.sections.map((sec) => {
          const dest = destByAnchor[sec.anchor];
          return (
          <section key={sec.anchor} id={sec.anchor} className="scroll-mt-28 mb-16" data-testid={`blog-section-${sec.anchor}`}>
            <h2 className="font-serif text-3xl md:text-4xl tracking-tighter text-[#1C1C1C] mb-6">{sec.title}</h2>
            <div className="space-y-4 max-w-3xl">
              {sec.paragraphs.map((p, i) => (
                <p key={i} className="text-[15px] leading-relaxed text-[#1C1C1C]">{p}</p>
              ))}
            </div>
            {sec.highlights?.length > 0 && (
              <div className="mt-8 bg-white border border-[#E0DDD6] p-6 max-w-3xl">
                {sec.highlights_title && (
                  <p className="tcp-overline text-[#C29B62] mb-4">{sec.highlights_title}</p>
                )}
                <ul className="space-y-2">
                  {sec.highlights.map((h, i) => (
                    <li key={i} className="text-sm text-[#1C1C1C] flex items-start gap-2">
                      <CheckCircle size={16} weight="fill" className="text-[#C29B62] mt-0.5 shrink-0" />
                      <span>{h}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {dest && <DestinationCTA destination={dest} />}
          </section>
          );
        })}

        {/* Closing CTA */}
        <div className="bg-[#0A0A0B] text-[#F7F5F0] p-8 md:p-12 mt-12" data-testid="blog-cta">
          <p className="tcp-overline text-[#C29B62] mb-4">Plan with our travel desk</p>
          <h3 className="font-serif text-3xl md:text-4xl tracking-tighter mb-4">
            Ready to <em className="italic">go?</em>
          </h3>
          <p className="text-[#A09E9A] max-w-xl mb-6">
            Our team books these destinations every week and has access to consolidator fares not listed
            online. Tell us where and when — we'll handle the rest.
          </p>
          <div className="flex flex-wrap gap-4">
            <a href={`tel:${PHONE_TEL}`} className="btn-gold inline-flex items-center gap-2 text-sm tracking-[0.16em] uppercase" data-testid="blog-cta-call">
              <Phone size={16} weight="fill" /> Call {PHONE_DISPLAY}
            </a>
            <Link to="/" className="btn-outline-light text-xs tracking-[0.16em] uppercase inline-flex items-center gap-2" data-testid="blog-cta-search">
              Search flights <ArrowUpRight size={14} />
            </Link>
          </div>
        </div>
      </article>

      <Footer />
    </div>
  );
}
